import type { FC, KeyboardEventHandler, MouseEvent, MouseEventHandler } from 'react';
import React, { useEffect, useState } from 'react';
import { Button } from '@THC/components/Button';
import { MoreHorizontalIcon } from '@THC/components/Icons/More';
import T from 'i18n-react';
import kebabCase from 'lodash/kebabCase';

import {
	CalculatorIcon,
	ChecklistIcon,
	IdIcon,
	MoneyStackIcon,
	PaperIcon,
	VehicleLorryIcon,
} from '@motorway/mw-highway-code';

import { triggerDeliveredStatusSnowplowEvent } from 'Components/Account/Purchases/PurchasesVehicleList/PurchasesVehicleListContextMenu/PurchasesVehicleListContextMenu.helpers';
import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import texts from 'Components/texts.json';
import { FEATURES } from 'Context/FeaturesContext/FeaturesContextConsts';
import { useIsPurchaseSummaryAllowed } from 'Pages/purchase-summary/helpers/isPurchaseSummaryAllowed.helpers';
import type { VehicleState } from 'Pages/vehicles/helpers/vehicle.schema';
import {
	triggerCompleteMoreMenuClickedEvent,
	triggerCompletePurchasePaymentMenuClickedEvent,
	triggerCompletePurchasesCancellationMenuClickedEvent,
	triggerCompletePurchasesDeliveryDetailsMenuClickedEvent,
	triggerCompletePurchasesSellerDetailsMenuClickedEvent,
	triggerPurchasesCompletePurchaseSummaryButtonClickedEvent,
} from 'Services/analytics/events';
import { DropdownMenu, DropdownMenuPortal, DropdownMenuTrigger } from 'Storybook/peripheral/DropdownMenu';
import DropdownMenuContentList from 'Storybook/peripheral/DropdownMenu/components/DropdownMenuContentList/DropdownMenuContentList';
import type { DropdownMenuItemProps } from 'Storybook/peripheral/DropdownMenu/DropdownMenu.types';
import { AccountSlugs } from 'Types/account';
import { Variant } from 'Types/variant';
import type { VehicleAccountListItem } from 'Types/vehicle';
import { INSPECTION_APP_URL } from 'Utilities/consts';
import { applyCypressData, isE2ETestEnv } from 'Utilities/helpers';
import { useUpdateDeliveryStatus } from 'Utilities/hooks/account/useUpdateDeliveryStatus/useUpdateDeliveryStatus';
import type { UseSuccessToast } from 'Utilities/hooks/account/useUpdateDeliveryStatus/useUpdateDeliveryStatus.helpers';
import useFeatureToggle from 'Utilities/hooks/useFeatureToggle';
import useCheckIsMobilizePartner, { checkIsMobilizeEnquiry } from 'Utilities/hooks/useIsMobilizeDealer';
import usePaymentsMVPEnabled from 'Utilities/hooks/usePaymentsMVPEnabled';
import usePaymentsPermission from 'Utilities/hooks/usePaymentsPermission';
import { useZendesk } from 'Utilities/hooks/useZendesk';
import ROUTES from 'Utilities/routes';
import { SOLD_CATEGORY } from 'Utilities/vehicleState';

import { SNOWPLOW_EVENTS } from '../../../../../services/analytics/analytics.const';
import { vehiclePaymentEvents } from '../../../../../services/analytics/events/paymentEvents/vehiclePaymentEvents';
import { getMarkAsDeliveredAttributes } from '../../Purchases.helpers';

import styles from './PurchasesVehicleListContextMenu.module.scss';

T.setTexts(texts);

export type PurchasesVehicleListContextMenuProps = Pick<
	VehicleAccountListItem,
	'id' | 'prettyVrm' | 'sourcePartner' | 'state' | 'isDelivered' | 'userBid' | 'transportMetadata' | 'enquirySoldDate'
> & {
	setRowActive: () => void;
	setRowInactive: () => void;
	triggerSuccessToast: UseSuccessToast['triggerSuccessToast'];
};

const PurchasesVehicleListContextMenu: FC<PurchasesVehicleListContextMenuProps> = ({
	enquirySoldDate,
	id,
	isDelivered,
	prettyVrm,
	setRowActive,
	setRowInactive,
	sourcePartner,
	state,
	transportMetadata,
	triggerSuccessToast,
	userBid,
}) => {
	const { mutate: mutateDeliveryStatus } = useUpdateDeliveryStatus();
	const { slug } = useAccountRouter();

	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

	const hideDropdownMenu = () => setIsDropdownOpen(false);
	const onTriggerClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.stopPropagation();
		e.preventDefault();

		if (!isDropdownOpen) {
			triggerCompleteMoreMenuClickedEvent({ id });
		}

		setIsDropdownOpen((prevState) => !prevState);
	};

	const isMirageEnabled = useFeatureToggle(FEATURES.mirageServer);
	const isAwaitingConfirmation = state.slug === SOLD_CATEGORY.SOLD_WAIT;
	const isStateSoldDocumentReview = state.slug === SOLD_CATEGORY.SOLD_DOCUMENTS_REVIEWED;

	const shouldShowPurchaseSummary = useIsPurchaseSummaryAllowed({
		soldDate: enquirySoldDate,
		state: state as VehicleState,
	});

	const isPaymentsMVPEnabled = usePaymentsMVPEnabled();
	const hasPaymentsPermission = usePaymentsPermission();
	const isMobilizeDealer = useCheckIsMobilizePartner();

	// Cypress doesn't support SSR
	const isPaymentsLinkAnchorTag = !isMirageEnabled && !isE2ETestEnv();

	const showAdjustmentsAndCancellationsMenuItem = slug === AccountSlugs.COMPLETE;
	const showVehiclePaymentMenuItem =
		isStateSoldDocumentReview &&
		isPaymentsMVPEnabled &&
		hasPaymentsPermission &&
		!(isMobilizeDealer && checkIsMobilizeEnquiry(sourcePartner));
	const { getPrePopulatedAdjustmentsAndCancellationLink } = useZendesk();

	const adjustmentsAndCancellationLink = getPrePopulatedAdjustmentsAndCancellationLink({ vrm: prettyVrm });

	const onMenuItemClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = (e) => {
		e.stopPropagation();
		hideDropdownMenu();
	};

	const onContainerKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
		e.stopPropagation();

		if (e.key === 'Escape' && isDropdownOpen) {
			hideDropdownMenu();
		}
	};

	const list: DropdownMenuItemProps[] = [
		...(shouldShowPurchaseSummary
			? [
					{
						cypressId: cypressIds.buttons.purchasePack,
						href: ROUTES.PURCHASE_SUMMARY.as(id),
						icon: PaperIcon,
						id: kebabCase(ROUTES.PURCHASE_SUMMARY.as(id)),
						onClick: (e: MouseEvent<HTMLAnchorElement>) => {
							onMenuItemClick(e);

							triggerPurchasesCompletePurchaseSummaryButtonClickedEvent();
						},
						text: T.translate('account.moreMenu.purchaseSummary'),
					},
				]
			: []),
		{
			cypressId: cypressIds.buttons.purchasesCompleteSellerDetails,
			href: ROUTES.SELLER_DETAILS.as(id),
			icon: IdIcon,
			id: kebabCase(ROUTES.SELLER_DETAILS.as(id)),
			onClick: (e: MouseEvent<HTMLAnchorElement>) => {
				onMenuItemClick(e);

				triggerCompletePurchasesSellerDetailsMenuClickedEvent({ id });
			},
			text: T.translate('account.moreMenu.sellerDetails'),
		},
		{
			cypressId: cypressIds.buttons.purchasesCompleteDeliveryOptions,
			href: ROUTES.DELIVERY.as(id),
			icon: VehicleLorryIcon,
			id: kebabCase(ROUTES.DELIVERY.as(id)),
			onClick: (e: MouseEvent<HTMLAnchorElement>) => {
				onMenuItemClick(e);

				triggerCompletePurchasesDeliveryDetailsMenuClickedEvent({ id });
			},
			text: T.translate('account.moreMenu.deliveryDetails'),
		},
		...(transportMetadata?.inspectionReportReady
			? [
					{
						cypressId: cypressIds.buttons.inspectionReportLink,
						href: `${INSPECTION_APP_URL}/${id}`,
						icon: ChecklistIcon,
						id: `inspection-report-${id}`,
						onClick: (e: MouseEvent<HTMLAnchorElement>) => {
							onMenuItemClick(e);
						},
						text: T.translate('account.moreMenu.inspectionReport'),
					},
				]
			: []),
		...(showVehiclePaymentMenuItem
			? [
					{
						cypressId: cypressIds.buttons.purchasesCompletePayments,
						href: ROUTES.VEHICLE_PAYMENT.as(id),
						icon: MoneyStackIcon,
						id: kebabCase(ROUTES.VEHICLE_PAYMENT.as(id)),
						isAnchorTag: isPaymentsLinkAnchorTag,
						onClick: (e: MouseEvent<HTMLAnchorElement>) => {
							onMenuItemClick(e);

							triggerCompletePurchasePaymentMenuClickedEvent({ id });
							vehiclePaymentEvents<{
								button_label: string;
								label: string;
								url: string;
							}>({
								customData: {
									button_label: `${T.translate('account.moreMenu.vehiclepayment')}`,
									label: `${id}`,
									url: '/vehiclepayment',
								},
								name: 'Vehicle payment menu clicked',
								schemaName: SNOWPLOW_EVENTS.CTA_CLICKED.SCHEMA,
								version: SNOWPLOW_EVENTS.CTA_CLICKED.VERSION,
							});
						},
						text: T.translate('account.moreMenu.vehiclepayment'),
					},
				]
			: []),
		...(showAdjustmentsAndCancellationsMenuItem
			? [
					{
						cypressId: cypressIds.buttons.purchasesCompleteAdjustmentsAndCancellations,
						icon: CalculatorIcon,
						id: `adjustmentsAndCalculations-${id}`,
						onClick: (e: MouseEvent<HTMLButtonElement>) => {
							onMenuItemClick(e);

							triggerCompletePurchasesCancellationMenuClickedEvent({ id });

							/* Unique window name means that the browser always opens a new window
							 * instead of reusing the same window if window.open is called multiple
							 * times with the same URL.
							 */
							const uniqueWindowName = new Date().getTime().toString();
							window.open(adjustmentsAndCancellationLink, uniqueWindowName);
						},
						text: T.translate('account.moreMenu.amendOrCancel'),
					},
				]
			: []),

		{
			...getMarkAsDeliveredAttributes({ id, isDelivered }),
			onClick: (e: MouseEvent<HTMLButtonElement>) => {
				onMenuItemClick(e);

				const deliveredStatus = !isDelivered;

				mutateDeliveryStatus(
					{
						deliveredStatus,
						enquiryId: userBid?.id,
					},
					{
						onSuccess: (_, variables) => {
							triggerSuccessToast(_, variables);
						},
					},
				);

				triggerDeliveredStatusSnowplowEvent(deliveredStatus);
			},
		},
	];

	useEffect(() => {
		if (isDropdownOpen) {
			setRowActive();
		} else {
			setRowInactive();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDropdownOpen]);

	if (isAwaitingConfirmation) {
		return null;
	}

	return (
		<div className={styles.contextMenuContainer} onKeyDown={onContainerKeyDown}>
			<DropdownMenu open={isDropdownOpen}>
				<DropdownMenuTrigger>
					<Button
						{...applyCypressData(cypressIds.buttons.moreMenu)}
						data-thc-button
						data-state={isDropdownOpen ? 'open' : 'closed'}
						icon={MoreHorizontalIcon}
						label="Context dropdown menu"
						onClick={onTriggerClick}
						showLabel={false}
						size="small"
						variant="tertiary"
					/>
				</DropdownMenuTrigger>
				<DropdownMenuPortal>
					<DropdownMenuContentList
						align="end"
						alignOffset={16}
						cypressId={cypressIds.containers.accountMoreMenuContent}
						list={list}
						onEscapeKeyDown={hideDropdownMenu}
						onInteractOutside={hideDropdownMenu}
						sideOffset={10}
						variant={Variant.SECONDARY}
					/>
				</DropdownMenuPortal>
			</DropdownMenu>
		</div>
	);
};

export { PurchasesVehicleListContextMenu };
